import { defineNuxtPlugin, useRuntimeConfig, useRouter, nextTick } from '#imports'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import { watch } from 'vue'
import { isNative, getPlatform } from '@/src/utils/platform'
import { DISABLE_POSTHOG, getReleaseString } from '~/src/constants'
import { useAppTracking } from '~/composables/useAppTracking'
import { useLogger } from '../composables/useLogger'

export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()
  const logger = useLogger()
  const authStore = useAuthStore()
  const { requestTrackingPermission, getTrackingStatus } = useAppTracking()

  // Debug platform function
  const debugPlatform = async () => {
    if (isNative()) {
      const platform = getPlatform()
      const userAgent = navigator.userAgent
      logger.debug('Posthog: Debug Platform:', {
        platform,
        userAgent,
        isNative: isNative(),
        isIPad: userAgent.includes('iPad') || (userAgent.includes('Mac') && 'ontouchend' in document)
      })

      const { status } = await getTrackingStatus()
      logger.debug('Posthog: Tracking status:', status)
      return { platform, userAgent, status }
    }
    return null
  }

  // Always add debug function to window
  if (typeof window !== 'undefined') {
    (window as any).debugNestorPlatform = debugPlatform
  }

  if (DISABLE_POSTHOG) {
    return {
      provide: {
        posthog: () => undefined
      }
    }
  }

  // Disable PostHog in development
  if (process.env.NODE_ENV === 'development') {
    return {
      provide: {
        posthog: () => undefined
      }
    }
  }

  try {
    // Check tracking permissions for iOS
    if (isNative() && getPlatform() === 'ios') {
      const { status } = await getTrackingStatus()
      logger.debug('Posthog: Tracking status:', status)
      
      if (status === 'notDetermined') {
        const { status: newStatus } = await requestTrackingPermission()
        if (newStatus !== 'authorized') {
          return {
            provide: {
              posthog: () => undefined
            }
          }
        }
      } else if (status !== 'authorized') {
        return {
          provide: {
            posthog: () => undefined
          }
        }
      }
    }

    // Base configuration for PostHog
    const posthogConfig = {
      api_host: config.public.posthogHost || 'https://eu.i.posthog.com',
      persistence: 'localStorage',
      capture_pageview: false,
      autocapture: false,
      disable_session_recording: false,
      capture_pageleave: false,
      loaded: (posthog: any) => {
        logger.debug('Posthog: Loaded with config')
        if (config.public.isDev) posthog.debug()
      }
    } as const

    // Initialize PostHog
    const posthogClient = posthog.init(config.public.posthogPublicKey, posthogConfig)

    // Set initial anonymous ID if no existing distinct_id
    if (!posthog.get_distinct_id()) {
      posthog.reset()
    }

    // Native app specific configuration
    if (isNative()) {
      const platform = getPlatform()
      
      // Set up properties for native app
      posthog.register({
        $app_platform: platform,
        $app_version: getReleaseString(),
        isNativeApp: true,
        environment: process.env.NODE_ENV
      })
    } else {
      posthog.register({
        $app_platform: 'web',
        $app_version: getReleaseString(),
        environment: process.env.NODE_ENV,
      })
    }

    // Watch for user identification changes
    watch(
      () => authStore.idForPostHogLogging,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          logger.debug('Posthog: Identifying user:', newId)
          posthog.identify(newId)
        } else if (!newId && oldId) {
          logger.debug('Posthog: Resetting identity')
          posthog.reset()
        }
      },
      { immediate: true }
    )

    // Enhanced pageview tracking
    const router = useRouter()
    router.afterEach((to) => {
      nextTick(() => {
        const screenName = to.name?.toString() || to.path.split('/').filter(Boolean).join('_') || 'home'
        const properties: Record<string, any> = {
          $screen_name: `${screenName}_screen`,
          isNativeApp: isNative(),
        }

        // For native apps, add URL transformation
        if (isNative() && to.fullPath) {
          properties.$current_url = to.fullPath
        }

        posthog.capture('$screen', properties)
      })
    })

    // Track user role changes
    watch(
      () => authStore.idForPostHogLogging,
      (newValue, oldValue) => {
        if (newValue !== oldValue && authStore.idForPostHogLogging) {
          logger.debug('Posthog: User role changed:', newValue)
          posthog.identify(authStore.idForPostHogLogging)
        }
      },
      { immediate: true }
    )

    return {
      provide: {
        posthog: () => posthogClient
      }
    }
  } catch (error) {
    logger.logErrorCaught('PostHog: Failed to initialize', 'posthog.client.ts', error)
    return {
      provide: {
        posthog: () => undefined
      }
    }
  }
}) 